import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { openSnackbar } from 'api/snackbar';
import { requestAbhaMobileUpdateOtp, verifyAbhaMobileUpdate } from 'utils/apiHelper';
import {
  INVALID_OTP_MESSAGE,
  MAX_OTP_RESENDS,
  MAX_RESEND_REACHED_MESSAGE,
  OTP_RESEND_SECONDS,
  extractAbhaErrorMessage,
  isFailedAuthResult,
  maskMobile,
  normalizeAbhaNumber,
  otpVerificationErrorMessage
} from 'utils/abhaUiUtils';

const notify = (message, color) => openSnackbar({ open: true, message, variant: 'alert', alert: { color } });

/**
 * ABDM M1 obs 31 (verification using mobile number): lets the user change
 * the verified ABHA's mobile number. ABDM sends an OTP to the new number;
 * once verified, the ABHA profile / card and the HMS patient carry it.
 */
// `fixedMobile`: the number to put on the ABHA is chosen elsewhere (the
// patient form's mobile field); this only verifies it with ABDM's OTP.
export default function AbhaUpdateMobile({ xToken, abhaNumber, currentMobile, onUpdated, fixedMobile, title }) {
  const [typedMobile, setMobile] = useState('');
  const mobile = fixedMobile !== undefined ? String(fixedMobile || '').replace(/\D/g, '').slice(-10) : typedMobile;
  const [otp, setOtp] = useState('');
  const [txnId, setTxnId] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [resendCount, setResendCount] = useState(0);

  useEffect(() => {
    if (resendTimer <= 0) return undefined;
    const timerId = setTimeout(() => setResendTimer((prev) => prev - 1), 1000);
    return () => clearTimeout(timerId);
  }, [resendTimer]);

  const isValidMobile = /^\d{10}$/.test(mobile);
  const isSameMobile = String(currentMobile || '').replace(/\D/g, '').endsWith(mobile) && isValidMobile;
  const resendLimitReached = resendCount >= MAX_OTP_RESENDS;

  const reset = () => {
    setTxnId('');
    setOtp('');
    setResendTimer(0);
    setResendCount(0);
  };

  const handleSendOtp = async () => {
    const isResend = Boolean(txnId);
    if (isResend && resendLimitReached) {
      notify(MAX_RESEND_REACHED_MESSAGE, 'warning');
      return;
    }
    setIsSending(true);
    try {
      const response = await requestAbhaMobileUpdateOtp({ xToken, mobile });
      const nextTxnId = response?.txnId || response?.txn_id;
      if (!nextTxnId) throw new Error(extractAbhaErrorMessage({ response: { data: response } }, 'Failed to send OTP'));
      setTxnId(nextTxnId);
      setOtp('');
      setResendTimer(OTP_RESEND_SECONDS);
      if (isResend) setResendCount((prev) => prev + 1);
      notify(response?.message || `OTP sent to ${maskMobile(mobile)}`, 'success');
    } catch (error) {
      notify(extractAbhaErrorMessage(error, 'Failed to send OTP'), 'error');
    } finally {
      setIsSending(false);
    }
  };

  const handleVerify = async () => {
    setIsVerifying(true);
    try {
      const response = await verifyAbhaMobileUpdate({
        xToken,
        txnId,
        otp,
        mobile,
        abhaNumber: normalizeAbhaNumber(abhaNumber)
      });
      if (isFailedAuthResult(response)) throw new Error(INVALID_OTP_MESSAGE);
      notify(response?.message || 'Mobile number updated', 'success');
      onUpdated?.(mobile, response);
      setMobile('');
      reset();
    } catch (error) {
      notify(otpVerificationErrorMessage(error), 'error');
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" gutterBottom>
        {title || 'Update mobile number'}
      </Typography>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-start' }}>
          <TextField
            fullWidth
            size="small"
            label="New mobile number"
            value={mobile}
            disabled={Boolean(txnId) || fixedMobile !== undefined}
            onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
            inputProps={{ inputMode: 'numeric', maxLength: 10 }}
            error={mobile.length > 0 && (!isValidMobile || isSameMobile)}
            helperText={
              mobile.length > 0 && !isValidMobile
                ? 'Please enter a valid 10-digit mobile number'
                : isSameMobile
                  ? 'This is already the ABHA mobile number'
                  : ''
            }
          />
          {!txnId ? (
            <Button
              variant="outlined"
              onClick={handleSendOtp}
              disabled={!isValidMobile || isSameMobile || isSending}
              startIcon={isSending ? <CircularProgress size={18} color="inherit" /> : null}
              sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              Send OTP
            </Button>
          ) : (
            <Button color="inherit" onClick={reset} sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
              Change number
            </Button>
          )}
        </Stack>
        {txnId && (
          <>
            <TextField
              fullWidth
              size="small"
              label={`OTP sent to ${maskMobile(mobile)}`}
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputProps={{ inputMode: 'numeric', maxLength: 6 }}
            />
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                onClick={handleVerify}
                disabled={otp.length !== 6 || isVerifying}
                startIcon={isVerifying ? <CircularProgress size={18} color="inherit" /> : null}
              >
                Verify & Update
              </Button>
              <Button
                variant="outlined"
                onClick={handleSendOtp}
                disabled={isSending || resendTimer > 0 || resendLimitReached}
              >
                {resendTimer > 0 ? `Resend OTP in ${resendTimer}s` : 'Resend OTP'}
              </Button>
            </Stack>
            <Typography variant="caption" color={resendLimitReached ? 'error' : 'text.secondary'}>
              {resendLimitReached ? MAX_RESEND_REACHED_MESSAGE : `Resend attempts left: ${MAX_OTP_RESENDS - resendCount}`}
            </Typography>
          </>
        )}
      </Stack>
    </Box>
  );
}

AbhaUpdateMobile.propTypes = {
  xToken: PropTypes.string,
  abhaNumber: PropTypes.string,
  currentMobile: PropTypes.string,
  onUpdated: PropTypes.func,
  fixedMobile: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  title: PropTypes.string
};
